import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  getSessionCookieToken,
  remediationStatus,
  type InvestigationDetail,
  type ListInvestigationsResponse,
} from '../api/client'

const IN_FLIGHT_STATES = new Set(['planning', 'executing'])
// Must outlast the backend's in-flight windows so polling doesn't freeze on a
// slow-but-alive run: RCA stale = 25 min (INVESTIGATION_STALE_AFTER_SECONDS),
// remediation stale = 30 min. 31 min covers both with margin.
const POLL_CAP_MS = 31 * 60 * 1000

function isRemediationInFlight(data: InvestigationDetail | undefined): boolean {
  if (!data?.remediation) return false
  return IN_FLIGHT_STATES.has(remediationStatus(data.remediation))
}

// True while the RCA itself is still being generated (row created up front with
// status="running", before persist flips it to "complete"/"failed").
function isRcaInFlight(data: InvestigationDetail | undefined): boolean {
  return data?.status === 'running'
}

export function useInvestigations(page = 1, pageSize = 20) {
  const { getToken } = useAuth()
  // Org in the key so a workspace switch never serves the previous org's rows.
  const { organization } = useOrganization()

  return useQuery({
    queryKey: ['investigations', organization?.id ?? 'none', page, pageSize],
    queryFn: async () => {
      const token = getSessionCookieToken() ?? await getToken()
      if (!token) throw new Error('Not authenticated')
      return api.listInvestigations(token, { page, page_size: pageSize })
    },
    // Poll while any investigation is still running or has a remediation attempt in flight
    // (e.g. after a Retry) so status and triage progress update live; idle otherwise.
    refetchInterval: (query) => {
      const data = query.state.data as ListInvestigationsResponse | undefined
      const active = data?.items?.some(
        (inv) =>
          inv.status === 'running' ||
          inv.remediation_status === 'pending' ||
          inv.remediation_status === 'running',
      )
      return active ? 5000 : false
    },
  })
}

export function useInvestigation(id: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: ['investigation', organization?.id ?? 'none', id],
    queryFn: async () => {
      const token = getSessionCookieToken() ?? await getToken()
      if (!token) throw new Error('Not authenticated')
      return api.getInvestigation(token, id)
    },
    enabled: Boolean(id),
    // Poll every 3s while the RCA is still generating or a remediation attempt is
    // in flight; stop at terminal state or the 10 min cap.
    refetchInterval: (query) => {
      const data = query.state.data as InvestigationDetail | undefined
      if (!isRcaInFlight(data) && !isRemediationInFlight(data)) return false
      const elapsed = Date.now() - (query.state.dataUpdatedAt ?? 0)
      if (elapsed >= POLL_CAP_MS) return false
      return 3000
    },
  })
}
